import { vehicleById, type EntityId, type MatchState, type VehicleState } from '../types';
import type { VehiclePerceptionProfile } from './VehicleController';

export function validateVehiclePerception(profile: VehiclePerceptionProfile): void {
  for (const [name, value] of [
    ['maxTeammates', profile.maxTeammates],
    ['maxOpponents', profile.maxOpponents],
    ['minOpponents', profile.minOpponents ?? 0],
  ] as const) {
    if (!Number.isSafeInteger(value) || value < 0) {
      throw new Error(`Vehicle perception ${name} must be a non-negative safe integer.`);
    }
  }
  if ((profile.minOpponents ?? 0) > profile.maxOpponents) {
    throw new Error('Vehicle perception minOpponents cannot exceed maxOpponents.');
  }
}

export function projectVehiclePerception(
  state: MatchState,
  entityId: EntityId,
  profile: VehiclePerceptionProfile,
): MatchState | null {
  const self = vehicleById(state, entityId);
  const teammates: VehicleState[] = [];
  const opponents: VehicleState[] = [];
  for (const vehicle of state.vehicles) {
    if (vehicle.entityId === entityId) continue;
    (vehicle.team === self.team ? teammates : opponents).push(vehicle);
  }

  const visibleTeammates = selectNearestPreservingOrder(state, teammates, profile.maxTeammates);
  const visibleOpponents = selectNearestPreservingOrder(state, opponents, profile.maxOpponents);
  if (visibleOpponents.length < (profile.minOpponents ?? 0)) return null;

  return {
    ...state,
    vehicles: [
      self,
      ...visibleTeammates,
      ...visibleOpponents,
    ],
  };
}

function selectNearestPreservingOrder(
  state: MatchState,
  candidates: readonly VehicleState[],
  limit: number,
): VehicleState[] {
  if (limit === 0) return [];
  if (candidates.length <= limit) return [...candidates];

  const selectedIndices = candidates
    .map((vehicle, index) => ({
      index,
      entityId: vehicle.entityId,
      distance: squaredDistanceToBall(state, vehicle),
    }))
    .sort((left, right) => left.distance - right.distance || left.entityId - right.entityId)
    .slice(0, limit)
    .map(({ index }) => index)
    .sort((left, right) => left - right);

  return selectedIndices.map((index) => {
    const vehicle = candidates[index];
    if (!vehicle) throw new Error(`Selected vehicle index ${index} is unavailable.`);
    return vehicle;
  });
}

function squaredDistanceToBall(state: MatchState, candidate: VehicleState): number {
  const dx = state.ball.position.x - candidate.car.position.x;
  const dy = state.ball.position.y - candidate.car.position.y;
  const dz = state.ball.position.z - candidate.car.position.z;
  return dx * dx + dy * dy + dz * dz;
}
